import { Provider } from '@angular/core';
import {
  CURRENT_MAIN_MENU_ITEM,
  CURRENT_MAIN_MENU_TREE_ITEM,
  LUNE_MAIN_MENU_ITEM_ACTIVE,
  MAIN_MENU,
  MAIN_MENU_BREADCRUMBS,
  MAIN_MENU_ITEMS,
} from '@nx-icilalune/data/icilalune/tokens';
import { select, Store } from '@ngrx/store';
import { selectEntitiesByType, selectMenu } from '@nx-icilalune/data/access';
import {
  Blog,
  Client,
  contextActualites,
  contextBlog,
  contextClients,
  contextSolutions,
  MainMenuItem,
  MainMenuTreeItem,
  News,
  Page,
  Project,
  Solution,
} from '@nx-icilalune/data/icilalune/types';
import { ROUTE_IS_HOME, ROUTER_URL } from '@icilalune/router';
import {
  combineLatest,
  filter,
  map,
  Observable,
  shareReplay,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DrupalNode, Tree, TreeItem } from '@nx-icilalune/drupal-types';
import { ROUTED_CONTENT } from './route.providers';
import { isEqual } from '@madeinlune/ngx-operators';
import {
  DETAIL_BREADCRUMBS,
  DETAIL_FEATURE_CURRENT_MENU_ITEM,
  DETAIL_FEATURE_CURRENT_MENU_TREE_ITEM,
  DETAIL_FEATURE_CURRENT_MENU_TREE_ITEM_PARENT,
  MADE_IN_LUNE_MENU_ITEM,
} from '@icilalune/detail-feature/tokens';
import { ELEVATOR_CONTEXT } from '@icilalune/elevator-feature/tokens';

function searchTree<T>(
  element: Tree<T> | TreeItem<T>,
  id: string
): TreeItem<T> | null {
  if (element instanceof TreeItem && element.ref.id == id) {
    return element;
  } else if (element.items != null) {
    let i;
    let result: TreeItem<T> | null = null;
    for (i = 0; result == null && i < element.items.length; i++) {
      result = searchTree<T>(element.items[i] as TreeItem<T>, id);
    }
    return result;
  }
  return null;
}

export const MAIN_MENU_PROVIDERS: Provider[] = [
  {
    provide: MAIN_MENU,
    deps: [Store],
    useFactory: (store: Store<any>) => {
      return store.pipe(select(selectMenu('main')));
    },
  },
  {
    provide: MAIN_MENU_ITEMS,
    deps: [Store],
    useFactory: (store: Store<any>) => {
      return store.pipe(
        select(selectEntitiesByType<MainMenuItem>(MainMenuItem.type))
      );
    },
  },
  {
    provide: CURRENT_MAIN_MENU_ITEM,
    deps: [MAIN_MENU_ITEMS, ROUTER_URL, ROUTED_CONTENT],
    useFactory: (
      mainMenuItems$: Observable<MainMenuItem[]>,
      routerUrl$: Observable<string>,
      routedContent$: Observable<DrupalNode | undefined>
    ) => {
      return combineLatest([routerUrl$, mainMenuItems$, routedContent$]).pipe(
        isEqual(),
        map(([url, mainMenuItems, routedContent]) => {
          if (routedContent) {
            if (routedContent.type === Project.type) {
              const project = routedContent as Project;
              if (project.businessUnitLabel === 'apps') {
                return mainMenuItems.find(
                  (m) => m.id === '08fec313-e6b1-42db-b71f-c7c0f1bda5fa'
                );
              } else if (project.businessUnitLabel === 'motion design') {
                return mainMenuItems.find(
                  (m) => m.id === 'b6482f43-32c0-4a43-a37c-bfc52102d6c8'
                );
              }
            } else if (routedContent.type === News.type) {
              return mainMenuItems.find(
                (m) => m.color === contextActualites && !!m.parentId
              );
            } else if (routedContent.type === Blog.type) {
              return mainMenuItems.find(
                (m) => m.color === contextBlog && !!m.parentId
              );
            } else if (routedContent.type === Solution.type) {
              return mainMenuItems.find(
                (m) => m.color === contextSolutions && !!m.parentId
              );
            } else if (routedContent.type === Client.type) {
              return mainMenuItems.find((m) => {
                return m.color === contextClients && !!m.parentId;
              });
            } else if (routedContent.type === Page.type) {
              const page: Page = routedContent;
              if (page.template === 'list:clients') {
                return mainMenuItems.find((m) => {
                  return m.color === contextClients && !!m.parentId;
                });
              } else if (page.template === 'list:news') {
                return mainMenuItems.find((m) => {
                  return m.color === contextActualites && !m.parentId;
                });
              } else if (page.template === 'list:blog') {
                return mainMenuItems.find((m) => {
                  return m.color === contextBlog && !m.parentId;
                });
              }
            }
          }
          const urlWithoutTrailerSlash =
            url.charAt(url.length - 1) === '/' ? url.slice(0, -1) : url;
          const menuItem = mainMenuItems.find((m) => {
            return m.path === url;
          });
          return menuItem;
        }),
        isEqual(),
        shareReplay(1)
      );
    },
  },
  {
    provide: LUNE_MAIN_MENU_ITEM_ACTIVE,
    deps: [CURRENT_MAIN_MENU_ITEM],
    useFactory: (currentMainMenuItem$: Observable<MainMenuItem>) => {
      return currentMainMenuItem$.pipe(
        filter((currentMainMenuItem) => !!currentMainMenuItem),
        map((currentMainMenuItem) => currentMainMenuItem.color === 'lune')
      );
    },
  },
  {
    provide: CURRENT_MAIN_MENU_TREE_ITEM,
    deps: [CURRENT_MAIN_MENU_ITEM, MAIN_MENU],
    useFactory: (
      currentMainMenuItem$: Observable<MainMenuItem>,
      mainMenu$: Observable<Tree<MainMenuTreeItem>>
    ) => {
      return combineLatest([currentMainMenuItem$, mainMenu$]).pipe(
        tap(([currentMainMenuItem, mainMenu]) => {}),
        filter(([currentMainMenuItem, mainMenu]) => {
          return !!currentMainMenuItem && !!mainMenu;
        }),
        map(([currentMainMenuItem, mainMenu]) => {
          return searchTree<MainMenuTreeItem>(mainMenu, currentMainMenuItem.id);
        })
      );
    },
  },
  {
    provide: MAIN_MENU_BREADCRUMBS,
    deps: [MAIN_MENU_ITEMS, CURRENT_MAIN_MENU_ITEM],
    useFactory: (
      mainMenuItems$: Observable<MainMenuItem[]>,
      currentMainMenuItem$: Observable<MainMenuItem>
    ) => {
      return currentMainMenuItem$.pipe(
        filter((currentMainMenuItem) => !!currentMainMenuItem),
        withLatestFrom(mainMenuItems$),
        map(([currentMainMenuItem, mainMenuItems]) => {
          let parentId: string | undefined = currentMainMenuItem.parentId;
          const breadcrumbs: MainMenuItem[] = [];
          breadcrumbs.push(currentMainMenuItem);
          // In Mission Control, we have Solutions, Apps and Motion Design as SubMenu Items and we don't want Mission Control to be considered as a parent of them
          while (parentId) {
            const mainMenuItemParent = mainMenuItems.find(
              (m) => m.id === parentId && m.color !== 'mission-control'
            );
            if (mainMenuItemParent) {
              parentId = mainMenuItemParent.parentId;
              breadcrumbs.push(mainMenuItemParent);
            } else {
              parentId = undefined;
            }
          }
          return breadcrumbs.reverse();
        }),
        isEqual(),
        shareReplay(1)
      );
    },
  },
  {
    provide: DETAIL_FEATURE_CURRENT_MENU_ITEM,
    deps: [CURRENT_MAIN_MENU_ITEM],
    useFactory: (mainMenuItem$: Observable<MainMenuItem>) => {
      return mainMenuItem$;
    },
  },
  {
    provide: DETAIL_FEATURE_CURRENT_MENU_TREE_ITEM,
    deps: [CURRENT_MAIN_MENU_TREE_ITEM],
    useFactory: (mainMenuItem$: Observable<MainMenuTreeItem>) => {
      return mainMenuItem$;
    },
  },
  {
    provide: DETAIL_FEATURE_CURRENT_MENU_TREE_ITEM_PARENT,
    deps: [CURRENT_MAIN_MENU_TREE_ITEM, MAIN_MENU],
    useFactory: (
      currentMainMenuItem$: Observable<MainMenuTreeItem>,
      mainMenu$: Observable<Tree<MainMenuTreeItem>>
    ) => {
      return currentMainMenuItem$.pipe(
        withLatestFrom(mainMenu$),
        filter(
          ([currentMainMenuItem, mainMenu]) =>
            !!currentMainMenuItem && !!mainMenu
        ),
        map(([currentMainMenuItem, mainMenu]) => {
          return searchTree<MainMenuTreeItem>(
            mainMenu,
            currentMainMenuItem.parent.id
          );
        })
      );
    },
  },
  {
    provide: MADE_IN_LUNE_MENU_ITEM,
    deps: [MAIN_MENU_ITEMS, MAIN_MENU],
    useFactory: (
      mainMenuItems$: Observable<MainMenuItem[]>,
      mainMenu$: Observable<Tree<MainMenuTreeItem>>
    ) => {
      return mainMenuItems$.pipe(
        withLatestFrom(mainMenu$),
        filter(([mainMenuItems, mainMenu]) => !!mainMenuItems && !!mainMenu),
        map(([mainMenuItems, mainMenu]) => {
          const madeInLuneItem = mainMenuItems.find(
            (m) => m.title === 'Made in Lune'
          );
          if (madeInLuneItem) {
            return searchTree<MainMenuTreeItem>(mainMenu, madeInLuneItem.id);
          }
          return undefined;
        })
      );
    },
  },
  {
    provide: DETAIL_BREADCRUMBS,
    deps: [MAIN_MENU_BREADCRUMBS],
    useFactory: (breadcrumbs$: Observable<MainMenuItem[]>) => {
      return breadcrumbs$;
    },
  },
  {
    provide: ELEVATOR_CONTEXT,
    deps: [CURRENT_MAIN_MENU_ITEM, ROUTE_IS_HOME],
    useFactory: (
      currentMenuItem$: Observable<MainMenuItem>,
      isHome$: Observable<boolean>
    ) => {
      return combineLatest([currentMenuItem$, isHome$]).pipe(
        filter(([currentMenuItem, isHome]) => currentMenuItem && !!isHome),
        map(([currentMenuItem, isHome]) => currentMenuItem.color)
      );
    },
  },
];
